<template>
  <div class="bg-white border border-gray-200 rounded-lg p-4 h-full flex flex-col">
    <!-- Шапка раздела: фильтры + Создать -->
    <div class="flex-shrink-0 flex flex-wrap gap-3 items-end mb-4">
      <div>
        <label class="block text-xs text-gray-600 mb-1">Статус</label>
        <div class="v-select-filter" style="width: 200px; flex: none;">
          <v-select
            v-model="filters.status"
            :options="statusOptions"
            :reduce="o => o.value"
            label="label"
            placeholder="Все"
          />
        </div>
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Дата с</label>
        <input
          type="date"
          v-model="filters.date_from"
          class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Дата по</label>
        <input
          type="date"
          v-model="filters.date_to"
          class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Номер оценки</label>
        <input
          type="text"
          v-model="filters.number"
          placeholder="Номер..."
          class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <button
        type="button"
        @click="resetFilters"
        class="self-end h-[39px] inline-flex items-center px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium"
      >
        Сбросить
      </button>
      <div class="ml-auto self-end">
        <button
          v-can-write
          type="button"
          @click="createNew"
          class="h-[39px] inline-flex items-center px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
        >
          Создать оценку
        </button>
      </div>
    </div>

    <!-- Таблица реестра -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>
      <div v-else-if="rows.length === 0" class="text-sm text-gray-500">Нет документов</div>
      <table v-else class="w-full text-sm">
        <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="px-2 py-2 text-left border-b">№ оценки</th>
            <th class="px-2 py-2 text-left border-b">Дата</th>
            <th class="px-2 py-2 text-left border-b">Ответственный</th>
            <th class="px-2 py-2 text-left border-b">Статус</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="border-t border-gray-200 hover:bg-blue-50 cursor-pointer"
            @click="openDocument(row)"
          >
            <td class="px-2 py-1.5">{{ row.number || ('#' + row.id) }}</td>
            <td class="px-2 py-1.5">{{ formatDate(row.assessment_date) }}</td>
            <td class="px-2 py-1.5">{{ userName(row.creator) }}</td>
            <td class="px-2 py-1.5">
              <span :class="statusClass(row.status)" class="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                {{ statusLabel(row.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Pagination
      class="flex-shrink-0 mt-3"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />
  </div>
</template>

<script>
import axios from 'axios';
import Pagination from '@/components/Pagination.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: { Pagination },
  props: {
    moduleId: { type: [String, Number], required: true },
  },
  data() {
    return {
      rows: [],
      loading: false,
      filters: { status: '', date_from: '', date_to: '', number: '' },
      statusOptions: [
        { value: 'draft', label: 'Новый' },
        { value: 'in_approval', label: 'В работе' },
        { value: 'approved', label: 'Согласован' },
        { value: 'cancelled', label: 'Отменён' },
      ],
      pagination: { currentPage: 1, perPage: 20, lastPage: 1, total: 0 },
    };
  },
  created() {
    // Синхронизация фильтров и страницы с URL — при возврате из формы состояние восстановится.
    // tab=risk держит активную вкладку рабочего стола СМК.
    this.urlManager = createUrlStateManager(this, {
      'filters.status': { default: '', urlKey: 'status' },
      'filters.date_from': { default: '', urlKey: 'date_from' },
      'filters.date_to': { default: '', urlKey: 'date_to' },
      'filters.number': { default: '', debounce: 300, urlKey: 'number' },
      'pagination.currentPage': { type: 'number', default: 1, urlKey: 'page' },
    }, {
      additionalQuery: { tab: 'risk' },
      onUpdate: () => this.load(),
    });
    this.load();
  },
  watch: {
    // Смена фильтра возвращает на первую страницу
    'filters.status'() { this.pagination.currentPage = 1; },
    'filters.date_from'() { this.pagination.currentPage = 1; },
    'filters.date_to'() { this.pagination.currentPage = 1; },
    'filters.number'() { this.pagination.currentPage = 1; },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/smk/risk-assessment', {
          params: {
            ...this.cleanFilters(),
            page: this.pagination.currentPage,
            per_page: this.pagination.perPage,
          },
        });
        if (data?.success) {
          const p = data.data || {};
          this.rows = p.data || [];
          this.pagination.currentPage = p.current_page || 1;
          this.pagination.lastPage = p.last_page || 1;
          this.pagination.total = p.total || 0;
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки списка оценок рисков');
      } finally {
        this.loading = false;
      }
    },
    cleanFilters() {
      const out = {};
      for (const [k, v] of Object.entries(this.filters)) {
        if (v !== '' && v != null) out[k] = v;
      }
      return out;
    },
    changePage(page) {
      this.pagination.currentPage = page; // urlManager обновит URL и вызовет load
    },
    resetFilters() {
      this.filters.status = '';
      this.filters.date_from = '';
      this.filters.date_to = '';
      this.filters.number = '';
      this.pagination.currentPage = 1;
    },
    returnQuery() {
      // Полное состояние реестра в URL — чтобы при возврате восстановить вкладку,
      // страницу и фильтры. Параметры видны в адресе формы.
      return {
        tab: 'risk',
        page: this.pagination.currentPage,
        ...this.cleanFilters(),
      };
    },
    createNew() {
      this.$router.push({
        path: `/smk-desk/${this.moduleId}/risk-assessment/new`,
        query: this.returnQuery(),
      });
    },
    openDocument(row) {
      // Передаём текущие фильтры/страницу в форму — при возврате реестр восстановится
      this.$router.push({
        path: `/smk-desk/${this.moduleId}/risk-assessment/${row.id}`,
        query: this.returnQuery(),
      });
    },
    userName(user) {
      if (!user) return '—';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || ('ID ' + user.id);
    },
    statusLabel(status) {
      return {
        draft: 'Новый',
        in_approval: 'В работе',
        approved: 'Согласован',
        cancelled: 'Отменён',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-gray-100 text-gray-700',
        in_approval: 'bg-yellow-100 text-yellow-700',
        approved: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
      }[status] || 'bg-gray-100 text-gray-700';
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    notifyError(error, fallback) {
      const msg = error?.response?.data?.message || fallback || 'Ошибка';
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(msg);
      } else {
        alert(msg);
      }
    },
  },
};
</script>
